import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, Download } from "lucide-react";
import { toast } from "sonner";
import { listArtifacts, bulkAssign, bulkApprove, exportArtifacts } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import ConfidenceMeter from "@/components/ConfidenceMeter";
import ContributorClassTag from "@/components/ContributorClassTag";
import PolicyFlag from "@/components/PolicyFlag";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_TABS = ["all", "new", "in_review", "disputed", "approved", "published", "revoked"];

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CandidateArtifacts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [reviewerInput, setReviewerInput] = useState("");
  const navigate = useNavigate();

  const fetchItems = () => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    listArtifacts(params)
      .then((r) => setItems(r.items || []))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load artifacts"))
      .finally(() => setLoading(false));
  };

  useEffect(fetchItems, [status, q]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  async function handleBulkAssign() {
    if (!reviewerInput.trim()) {
      toast.error("Enter a reviewer name");
      return;
    }
    try {
      const res = await bulkAssign([...selected], reviewerInput.trim());
      toast.success(`Assigned ${res.updated} artifact${res.updated !== 1 ? "s" : ""} to ${reviewerInput.trim()}`);
      setSelected(new Set());
      setAssignOpen(false);
      setReviewerInput("");
      fetchItems();
    } catch {
      toast.error("Failed to assign reviewer");
    }
  }

  async function handleBulkApprove() {
    try {
      const res = await bulkApprove([...selected]);
      const approvedCount = (res.approved || []).length;
      const skippedCount = (res.skipped || []).length;
      toast.success(`Approved ${approvedCount}.${skippedCount > 0 ? ` Skipped ${skippedCount} (policy flags).` : ""}`);
      setSelected(new Set());
      fetchItems();
    } catch {
      toast.error("Failed to bulk approve");
    }
  }

  async function handleExport() {
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q) params.q = q;
      const blob = await exportArtifacts(params);
      downloadBlob(blob, "artifacts-export.json");
    } catch {
      toast.error("Export failed");
    }
  }

  return (
    <div className="p-8 md:p-10 space-y-6" data-testid="artifacts-page">
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign reviewer</DialogTitle>
            <DialogDescription>
              Assign {selected.size} selected artifact{selected.size !== 1 ? "s" : ""} to a reviewer.
              Each artifact status will be set to &ldquo;in review&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1.5">Reviewer name</label>
              <input
                value={reviewerInput}
                onChange={(e) => setReviewerInput(e.target.value)}
                placeholder="e.g. L. Ibarra"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                onKeyDown={(e) => { if (e.key === "Enter") handleBulkAssign(); }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setAssignOpen(false)}
                className="rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAssign}
                className="rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
              >
                Assign
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="tracked-caps">Queue</div>
          <h2 className="mt-1 font-serif-display text-3xl">Candidate artifacts</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Operational queue of patterns awaiting review. Every row carries its own evidence record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-muted"
            data-testid="artifacts-export"
          >
            <Download className="h-3.5 w-3.5" /> Export
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm flex-1 max-w-md">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title or ID (e.g. CA-2041)"
            className="flex-1 bg-transparent outline-none"
            data-testid="artifacts-search"
          />
        </div>
        <div className="flex items-center gap-1 overflow-x-auto">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`artifacts-tab-${s}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-md border transition-colors ${
                status === s ? "bg-brand text-brand-foreground border-brand" : "bg-card border-border hover:bg-muted"
              }`}
            >
              {s === "all" ? "All" : s.replace("_", " ")}
            </button>
          ))}
        </div>
        <button className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-muted" data-testid="artifacts-filters">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
        </button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-4 py-2 text-sm">
          <span>{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setReviewerInput(""); setAssignOpen(true); }}
              className="rounded border border-border bg-card px-3 py-1 hover:bg-muted"
            >
              Assign reviewer
            </button>
            <button
              onClick={handleBulkApprove}
              className="rounded border border-brand bg-brand/5 text-brand px-3 py-1 hover:bg-brand/10"
            >
              Bulk approve
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="tracked-caps text-left">
              <th className="px-4 py-3 w-8"></th>
              <th className="px-4 py-3 font-medium">ID</th>
              <th className="px-4 py-3 font-medium">Title</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Domain</th>
              <th className="px-4 py-3 font-medium">Class</th>
              <th className="px-4 py-3 font-medium">Confidence</th>
              <th className="px-4 py-3 font-medium">Flags</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Reviewer</th>
              <th className="px-4 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skel-${i}`} className="border-t border-border">
                <td className="px-4 py-3"><Skeleton className="h-4 w-4 rounded" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-20" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-48" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-16" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-16" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-12" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-16" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-12" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-16" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-20" /></td>
                <td className="px-4 py-3"><Skeleton className="h-3 w-20" /></td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-16 text-center text-muted-foreground">
                <div className="font-serif-display text-xl">No candidate artifacts match</div>
                <div className="text-sm mt-1">Try widening the filter or clearing the search.</div>
              </td></tr>
            )}
            {!loading && items.map((a) => (
              <tr
                key={a.id}
                onClick={() => navigate(`/app/artifacts/${a.id}`)}
                data-testid={`artifact-row-${a.id}`}
                className="border-t border-border hover:bg-muted/40 transition-colors cursor-pointer"
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(a.id)}
                    onChange={() => toggle(a.id)}
                    data-testid={`row-select-${a.id}`}
                    className="h-3.5 w-3.5 rounded border-border"
                  />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.id}</td>
                <td className="px-4 py-3 max-w-sm">
                  <Link to={`/app/artifacts/${a.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline" data-testid={`row-title-${a.id}`}>{a.title}</Link>
                </td>
                <td className="px-4 py-3">{a.type}</td>
                <td className="px-4 py-3 text-muted-foreground">{a.domain}</td>
                <td className="px-4 py-3"><ContributorClassTag value={a.contributor_classes[0]} count={a.contributor_count} /></td>
                <td className="px-4 py-3"><ConfidenceMeter value={a.confidence} /></td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {a.policy_flags.map((f) => <PolicyFlag key={f} label={f} severity="medium" />)}
                  </div>
                </td>
                <td className="px-4 py-3"><StatusBadge value={a.status} /></td>
                <td className="px-4 py-3 text-xs">{a.assigned_reviewer}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtDate(a.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
